import { Link } from "react-router-dom";
import { Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OfferExportDialog } from "@/components/deals/OfferExportDialog";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { pct } from "@/components/deals/format";

export function OfferRulesPanel({ settings, onSaved }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setDraft(settings); }, [settings]);
  if (!settings || !draft) return null;
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const override = !!draft.deals_override_enabled;
  const pct = override ? draft.deals_smart_pc_pct : settings.smart_pc_pct;
  const roi = override ? draft.deals_smart_min_roi_pct : settings.smart_min_roi_pct;
  const save = async () => {
    setSaving(true);
    try {
      const s = await api.updateSettings({
        deals_override_enabled: override,
        deals_smart_pc_pct: Number(draft.deals_smart_pc_pct), deals_smart_min_roi_pct: Number(draft.deals_smart_min_roi_pct),
      });
      toast.success(override ? "Deals override saved · deals recomputing" : "Deals now follow the sitewide rules");
      onSaved && onSaved(s);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-[#E4E4E0] rounded-sm" data-testid="offer-rules-panel">
      <button onClick={() => setOpen(o => !o)} className="w-full px-3 py-2 flex items-center justify-between text-xs font-mono uppercase text-gray-600 hover:text-[#E3000B]" data-testid="offer-rules-toggle">
        <span className="flex items-center gap-2">
          <Receipt size={12} className="text-[#00A850]" />
          Smart Offer rules
          <span className={`text-[10px] normal-case tracking-normal px-1.5 rounded-sm font-bold ${override ? "bg-[#E3000B]/10 text-[#E3000B]" : "bg-[#00A850]/10 text-[#00A850]"}`} data-testid="offer-rules-source">
            {override ? "DEALS OVERRIDE" : "SITEWIDE"}
          </span>
          <span className="text-[10px] normal-case text-gray-400 tracking-normal">{Number(pct)}% of PriceCharting · min ROI {Number(roi)}% · round ${settings.round_to_multiple}</span>
        </span>
        <span className="text-gray-500">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="p-3 border-t border-[#E4E4E0] space-y-3">
          <div className="flex items-center gap-3">
            <Switch checked={override} onCheckedChange={(v) => set("deals_override_enabled", v)} data-testid="rule-deals-override" className="data-[state=checked]:bg-[#E3000B]" />
            <span className="font-mono text-xs text-gray-800">Manual override for Deals — use different Smart Offer dials here than the public site</span>
            <Link to="/settings" className="ml-auto font-mono text-[10px] text-[#0055BF] underline" data-testid="rule-sitewide-link">Edit sitewide rules →</Link>
          </div>
          {override && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <RuleField label="Deals: % of PriceCharting" hint="Only affects the Deals page" value={draft.deals_smart_pc_pct} onChange={(v) => set("deals_smart_pc_pct", v)} step="1" testId="rule-smart-pc-pct" />
              <RuleField label="Deals: min ROI %" hint="Cap so Amazon still returns this after fees" value={draft.deals_smart_min_roi_pct} onChange={(v) => set("deals_smart_min_roi_pct", v)} step="1" testId="rule-smart-min-roi" />
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="h-8 rounded-sm bg-[#E3000B] text-white font-bold uppercase tracking-wider text-xs hover:bg-[#C4000A] disabled:opacity-50" data-testid="rule-save">
              {saving ? "SAVING…" : "SAVE"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function RuleField({ label, hint, value, onChange, step, testId }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</span>
      <Input
        type="number"
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs"
        data-testid={testId}
      />
      <span className="text-[9px] font-mono text-gray-400 leading-tight">{hint}</span>
    </label>
  );
}

/**
 * OfferExportDialog — CLIENT-facing offer sheet builder.
 *
 * This is what the user sends to a seller who has asked "how much will you
 * pay for my sets?". It shows the set number, title, and the offer price
 * (max-buy for uncosted rows, list-recommended for costed ones), lets the
 * user tick which lines to include, tweak the offer amount in-line, and
 * optionally include the BrickLink lowest sealed price as a reference for
 * the seller. Amazon Buy Box / ASIN / internal ROI data is intentionally
 * kept OUT of the export — that's the user's private edge.
 */
